import React, { useState } from 'react';
import CommonLoader from '../../../../Components/CommonLoader';
import {
    risk_portal_ai_categorize,
    risk_portal_ai_duplicates,
    risk_portal_ai_risk_statement,
    risk_portal_ai_suggest_treatment,
} from '../../../../services';
import { Body, SubHeading, body, bodySmall, fontSize } from '../../../../Constants/Fonts';
import { extractItem, titleCase } from './constants';

// ── Icons (inline, stroke-based to match the portal's line-icon set) — the
// same set used by the "Identify Risk" FORESEE AI Assist card (Forms/NovaAiAssist).
const svg = (children) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const ICON_SPARK = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2l1.6 4.6a5 5 0 0 0 3.1 3.1L21.3 11l-4.6 1.6a5 5 0 0 0-3.1 3.1L12 20.3l-1.6-4.6a5 5 0 0 0-3.1-3.1L2.7 11l4.6-1.6a5 5 0 0 0 3.1-3.1L12 2z" />
        <path d="M19 3l.5 1.5L21 5l-1.5.5L19 7l-.5-1.5L17 5l1.5-.5L19 3z" opacity="0.85" />
    </svg>
);
const ICON_TAG = svg(<><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0l-7-7a2 2 0 0 1-.6-1.4V5a2 2 0 0 1 2-2h7.2a2 2 0 0 1 1.4.6l7 7a2 2 0 0 1 0 2.8z" /><circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" stroke="none" /></>);
const ICON_DUP = svg(<><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></>);
const ICON_DOC = svg(<><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></>);
const ICON_SHIELD = svg(<><path d="M12 3l7 3v5c0 4.5-3 8.3-7 9.5C8 19.3 5 15.5 5 11V6l7-3z" /><path d="M9.5 12l1.8 1.8L15 10" /></>);

// NovaAiSection — the four FORESEE AI assists (categorize / duplicates / ISO
// statement / suggest treatment), presented as the "FORESEE AI Assist" tile card
// (same layout as the Identify-Risk form) and rendering its result inline.
export default function NovaAiSection({ detail }) {
    const [running, setRunning] = useState(null);   // which action is in-flight
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const run = async (kind, callable) => {
        setRunning(kind); setError(null); setResult(null);
        try {
            const res = await callable();
            const d = extractItem(res);
            const inner = res?.statusCode?.error?.message || res?.errors?.message;
            if (inner) setError(inner);
            else if (d) setResult({ kind, ...d });
            else setError('The AI service returned an empty response — please try again.');
        } catch (e) {
            setError(e?.response?.data?.error?.message || e?.message || 'Service unavailable');
        } finally {
            setRunning(null);
        }
    };

    const desc = detail.risk;
    const actions = [
        { kind: 'categorize', label: 'Auto-Categorize', icon: ICON_TAG, hint: 'Classify into an ISO category', fn: () => risk_portal_ai_categorize({ description: desc }) },
        { kind: 'duplicates', label: 'Detect Duplicates', icon: ICON_DUP, hint: 'Find similar existing risks', fn: () => risk_portal_ai_duplicates({ description: desc }) },
        { kind: 'statement', label: 'Generate ISO Statement', icon: ICON_DOC, hint: 'Fill threat, vuln & scores', fn: () => risk_portal_ai_risk_statement({ description: desc }) },
        { kind: 'treatment', label: 'Suggest Treatment', icon: ICON_SHIELD, hint: 'Preview a treatment plan', fn: () => risk_portal_ai_suggest_treatment({ description: desc, risk_type: detail.risk_type, severity: detail.severity }) },
    ];

    return (
        <div className="rp1_ai_assist" style={{ marginTop: 0 }}>
            <div className="rp1_ai_assist_head">
                <span className="rp1_ai_spark" aria-hidden="true">{ICON_SPARK}</span>
                <div className="rp1_ai_assist_head_text">
                    <SubHeading variant={2} as="span" color="var(--color-text-primary)" style={{ margin: 0 }}>FORESEE AI Assist</SubHeading>
                    <Body as="span" variant={2} color="var(--color-text-muted)">Run FORESEE AI analyses on this risk's description.</Body>
                </div>
            </div>

            <div className="rp1_ai_grid">
                {actions.map((a) => {
                    const busy = running === a.kind;
                    return (
                        <button
                            key={a.kind}
                            type="button"
                            className={`rp1_ai_btn${busy ? ' rp1_ai_btn--busy' : ''}`}
                            onClick={() => run(a.kind, a.fn)}
                            disabled={!!running}
                        >
                            <span className="rp1_ai_btn_icon">{a.icon}</span>
                            <span className="rp1_ai_btn_text">
                                <span className="rp1_ai_btn_label">{busy ? 'Analyzing…' : a.label}</span>
                                <span className="rp1_ai_btn_hint">{a.hint}</span>
                            </span>
                        </button>
                    );
                })}
            </div>

            {running && <div style={{ marginTop: 14 }}><CommonLoader label="FORESEE is thinking…" /></div>}
            {error && <p style={bodySmall({ color: 'var(--color-danger)', margin: '14px 0 0' })}>{error}</p>}
            {result && <AiResult result={result} />}
        </div>
    );
}

function AiResult({ result }) {
    const { kind, method } = result;
    return (
        <div className="rp1_ai_result">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Body as="span" variant={2} weight="medium" color="var(--color-text-primary)" style={{ fontSize: fontSize.sm, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{titleCase(kind)}</Body>
                {method && <Body as="span" className="rp1_badge rp1_badge--neutral">{titleCase(method)}</Body>}
            </div>
            {kind === 'categorize' && (
                <p style={body({ color: 'var(--color-text-secondary)', margin: 0 })}>
                    Suggested category: <strong>{titleCase(result.category || result.risk_type)}</strong>
                    {result.confidence != null && ` (${Math.round((result.confidence || 0) * 100)}% confidence)`}
                </p>
            )}
            {kind === 'statement' && (
                <p style={body({ color: 'var(--color-text-secondary)', margin: 0, whiteSpace: 'pre-wrap' })}>{result.statement || result.risk_statement || '—'}</p>
            )}
            {kind === 'duplicates' && (
                (result.duplicates || []).length === 0
                    ? <p style={bodySmall({ color: 'var(--color-text-muted)', margin: 0 })}>No likely duplicates found.</p>
                    : <ul className="rp1_ai_list">{(result.duplicates || []).map((d, i) => (
                        <li key={i} style={body({ color: 'var(--color-text-secondary)' })}>
                            <strong>{d.risk_id || `#${d.id || i + 1}`}</strong> — {d.risk || d.description}
                            {d.score != null && <Body as="span" variant={2} color="var(--color-text-muted)"> ({Math.round((d.score || 0) * 100)}%)</Body>}
                        </li>
                    ))}</ul>
            )}
            {kind === 'treatment' && (
                Array.isArray(result.suggestions)
                    ? <ul className="rp1_ai_list">{result.suggestions.map((s, i) => (
                        <li key={i} style={body({ color: 'var(--color-text-secondary)' })}>{typeof s === 'string' ? s : (s.description || s.title)}</li>
                    ))}</ul>
                    : <p style={body({ color: 'var(--color-text-secondary)', margin: 0, whiteSpace: 'pre-wrap' })}>{result.suggestion || result.treatment || '—'}</p>
            )}
        </div>
    );
}
